import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  Facebook,
  Twitter,
  MessageCircle,
  ArrowRight,
} from "lucide-react";
import { motion, useScroll, useSpring } from "framer-motion";
import { ref, onValue } from "firebase/database";
import { rtdb } from "../config/firebase";

// Social Share Button
const SocialShareButton = ({ href, icon: Icon, hoverClasses }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={`w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm text-white/80 flex items-center justify-center transition-all duration-300 ${hoverClasses}`}
  >
    <Icon size={20} />
  </a>
);

export default function NewsDetail() {
  const { id } = useParams();
  const [newsData, setNewsData] = useState(null);
  const [otherNews, setOtherNews] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const beritaRef = ref(rtdb, "berita");
    onValue(beritaRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const list = Object.keys(data).map((key) => ({
          id: key,
          title: data[key].judul,
          content: data[key].keterangan,
          excerpt: data[key].keterangan?.slice(0, 100) + "...",
          date: data[key].tanggal + " " + data[key].waktu,
          category: "Berita",
          image: data[key].fotoUrl,
          author: { name: "Soney Raya", avatar: "/images/foto.png" }, // pakai logo lokal
        }));

        const current = list.find((n) => n.id === id);
        const others = list.filter((n) => n.id !== id);

        setNewsData(current || null);
        setOtherNews(others);
      }
    });
  }, [id]);

  if (!newsData) return <NotFoundPage />;

  return (
    <NewsArticle
      newsData={newsData}
      otherNews={otherNews}
      navigate={navigate}
    />
  );
}

function NewsArticle({ newsData, otherNews, navigate }) {
  const pageUrl = window.location.href;
  const contentRef = useRef(null);
  const { scrollYProgress } = useScroll({ container: contentRef });
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  return (
    <div className="h-screen w-full bg-gray-100 font-sans grid grid-cols-1 lg:grid-cols-2 overflow-hidden">
      {/* PANEL KIRI */}
      <motion.div
        initial={{ clipPath: "inset(0 100% 0 0)" }}
        animate={{ clipPath: "inset(0 0 0 0)" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        className="relative h-full w-full flex flex-col justify-between p-8 md:p-12 text-white"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-green-800/80 via-teal-800/70 to-green-900/80 z-0"></div>
        <img
          src={newsData.image}
          alt={newsData.title}
          className="absolute inset-0 w-full h-full object-cover z-[-1] mix-blend-multiply"
        />
        <div className="relative z-10">
          <button
            onClick={() => navigate(-1)}
            className="group flex items-center gap-3 text-white font-semibold bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full hover:bg-white/20 transition-all"
          >
            <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
            <span>Kembali</span>
          </button>
          <div className="flex items-center gap-4 mt-8">
            <img
              src={newsData.author.avatar}
              alt={newsData.author.name}
              className="w-14 h-14 rounded-full border-2 border-white/50"
            />
            <div>
              <p className="font-bold text-lg">{newsData.author.name}</p>
              <p className="text-white/70">Penulis Artikel</p>
            </div>
          </div>
        </div>
        <div className="relative z-10">
          <span className="inline-block px-4 py-1 bg-green-500/90 backdrop-blur-md rounded-full text-sm font-semibold tracking-wider mb-4 border border-white/30">
            {newsData.category || "General"}
          </span>
          <h1 className="text-4xl md:text-5xl font-bold leading-tight text-shadow">
            {newsData.title}
          </h1>
          <div className="flex items-center gap-6 text-lg opacity-90 mt-6">
            <div className="flex items-center gap-2">
              <Calendar size={18} />
              <span>{newsData.date}</span>
            </div>
          </div>
        </div>
        <div className="relative z-10 flex items-center gap-4">
          <span className="font-semibold">Bagikan:</span>
          <div className="flex items-center gap-3">
            <SocialShareButton
              href={`https://api.whatsapp.com/send?text=${encodeURIComponent(newsData.title + " " + pageUrl)}`}
              icon={MessageCircle}
              hoverClasses="hover:bg-green-500 hover:text-white"
            />
            <SocialShareButton
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`}
              icon={Facebook}
              hoverClasses="hover:bg-blue-800 hover:text-white"
            />
            <SocialShareButton
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(newsData.title)}`}
              icon={Twitter}
              hoverClasses="hover:bg-sky-500 hover:text-white"
            />
          </div>
        </div>
      </motion.div>

      {/* PANEL KANAN */}
      <motion.div
        initial={{ clipPath: "inset(0 0 0 100%)" }}
        animate={{ clipPath: "inset(0 0 0 0)" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        ref={contentRef}
        className="relative h-full bg-white overflow-y-auto flex flex-col custom-scrollbar"
      >
        <motion.div className="sticky top-0 h-2 bg-emerald-500 origin-left z-20" style={{ scaleX }} />
        <div className="p-8 md:p-12 lg:p-16">
          <article
            className="prose prose-lg prose-green max-w-none text-gray-800 leading-relaxed
                       first-letter:text-7xl first-letter:font-bold first-letter:text-emerald-600
                       first-letter:mr-4 first-letter:float-left"
            dangerouslySetInnerHTML={{ __html: newsData.content }}
          />
          <div className="pt-16 mt-12 border-t border-gray-200">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">
              Baca Juga Berita Lainnya
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {otherNews.slice(0, 4).map((item) => (
                <Link
                  to={`/news/${item.id}`}
                  key={item.id}
                  className="group bg-gray-50 rounded-2xl hover:shadow-xl transition-all duration-300 overflow-hidden transform hover:-translate-y-1"
                >
                  <div className="relative h-40">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-5">
                    <p className="text-sm font-semibold text-emerald-600 mb-2">{item.category}</p>
                    <h3 className="font-bold text-gray-900 leading-tight line-clamp-2 group-hover:text-emerald-700 transition-colors">
                      {item.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/news"
                className="inline-flex items-center gap-2 px-8 py-3 bg-emerald-600 text-white font-semibold rounded-full shadow-lg hover:bg-emerald-700 transition-all transform hover:scale-105"
              >
                <span>Lihat Semua Berita</span>
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 text-center p-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="text-4xl font-bold text-gray-800 mb-4">
          404 - Berita Tidak Ditemukan
        </h2>
        <p className="text-gray-600 mb-8">
          Maaf, artikel yang Anda cari tidak ada atau telah dihapus.
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-semibold rounded-lg shadow-md hover:bg-green-700 transition"
        >
          <ArrowLeft size={18} /> Kembali ke Beranda
        </Link>
      </motion.div>
    </div>
  );
}
